import { useEffect, useRef, useCallback } from 'react';

import { performanceMonitor } from '@/lib/performance-monitor';

/**
 * Hook for monitoring component render performance
 */
export function useRenderPerformance(componentName: string, enabled: boolean = process.env.NODE_ENV === 'development') {
  const renderStartTime = useRef<number>(0);
  const renderCount = useRef<number>(0);

  // Start measuring at the beginning of render
  if (enabled) {
    renderStartTime.current = performance.now();
    renderCount.current++;
  }

  useEffect(() => {
    if (!enabled) return;

    const renderTime = performance.now() - renderStartTime.current;
    
    // Record the metric
    performanceMonitor['recordMetric']({
      componentName,
      renderTime,
      timestamp: Date.now(),
    });

    // Warn about slow renders
    if (renderTime > 16) {
      console.warn(`Slow render: ${componentName} took ${renderTime.toFixed(2)}ms (render #${renderCount.current})`);
    }
  });

  return {
    renderCount: renderCount.current,
  };
}

/**
 * Hook for monitoring expensive operations
 */
export function useOperationPerformance() {
  const measureOperation = useCallback(<T>(
    operationName: string,
    operation: () => T
  ): T => {
    const startTime = performance.now();
    const result = operation();
    const endTime = performance.now();
    const duration = endTime - startTime;

    if (duration > 5) {
      console.warn(`Slow operation: ${operationName} took ${duration.toFixed(2)}ms`);
    }

    return result;
  }, []);

  const measureAsyncOperation = useCallback(async <T>(
    operationName: string,
    operation: () => Promise<T>
  ): Promise<T> => {
    const startTime = performance.now();
    const result = await operation();
    const endTime = performance.now();
    const duration = endTime - startTime;

    if (duration > 100) {
      console.warn(`Slow async operation: ${operationName} took ${duration.toFixed(2)}ms`);
    }

    return result;
  }, []);

  return {
    measureOperation,
    measureAsyncOperation,
  };
}

/**
 * Hook for monitoring memory usage
 */
export function useMemoryMonitor(componentName: string, enabled: boolean = process.env.NODE_ENV === 'development') {
  const initialMemory = useRef<number>(0);

  useEffect(() => {
    if (!enabled || !('memory' in performance)) return undefined;

    const memory = (performance as any).memory;
    initialMemory.current = memory.usedJSHeapSize;

    return () => {
      const finalMemory = memory.usedJSHeapSize;
      const memoryDiff = finalMemory - initialMemory.current;
      
      if (Math.abs(memoryDiff) > 1024 * 1024) { // 1MB threshold
        console.log(`Memory change in ${componentName}: ${(memoryDiff / 1024 / 1024).toFixed(2)}MB`);
      }
    };
  }, [componentName, enabled]);
}

/**
 * Hook for detecting unnecessary re-renders
 */
export function useRenderTracker(componentName: string, props: Record<string, any>, enabled: boolean = process.env.NODE_ENV === 'development') {
  const prevProps = useRef<Record<string, any>>();
  const renderCount = useRef<number>(0);

  if (enabled) {
    renderCount.current++;

    if (prevProps.current) {
      const changedProps = Object.keys(props).filter(
        key => prevProps.current![key] !== props[key]
      );

      if (changedProps.length === 0) {
        console.warn(`Unnecessary re-render in ${componentName} (render #${renderCount.current})`);
      } else if (changedProps.length > 0) {
        console.log(`${componentName} re-rendered due to props:`, changedProps);
      }
    }

    prevProps.current = { ...props };
  }

  return {
    renderCount: renderCount.current,
  };
}